import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import '../../styles/blitz.css';
import APIService from '../../services/APIService';
import ModeEmpty from './ModeEmpty';
import InterviewPrelude from './InterviewPrelude';
import * as sessionCache from './sessionCache';
import { logRapidfireQuestion, logRapidfireRound, scopeIds } from './memoryLog';
import { useDialog } from './ui/dialogs';
import { IconMic, IconStop } from './icons';

/*
 * BlitzRound — the TYPED blitz. Same one-liner questions as the spoken AI
 * Rapidfire round (/engine/rapidfire), typed against a clock the learner sets.
 *
 *   setup  → pick seconds per question; questions are cached per topic scope
 *   play   → Enter sends, Space dictates (streaming speech-to-text) when the
 *            box is empty, Skip banks a question for the end. No verdicts
 *            mid-round: answers are graded together at the END.
 *   grading→ one call to /engine/blitz/grade (keyword fallback offline)
 *   done   → assessment of the ANSWERED questions only; Go again / New / Done
 *
 * Leaving mid-round (sidebar, back button, mode switch) asks: See assessment ·
 * Leave (skip assessment) · Cancel — via the app's leave guard.
 */
const DEFAULT_SECONDS = 25;
const MIN_SECONDS = 10;
const MAX_SECONDS = 60;
const STAR_FACTOR = 1.4;      // behavioral (STAR) items get more time
const READ_MS = 1200;         // a beat to read the question before the clock runs
const ROUND_SIZE = 12;
const PREFS_KEY = 'nano_blitz_prefs_v1';

const loadPrefs = () => {
  try {
    const p = JSON.parse(localStorage.getItem(PREFS_KEY) || '{}');
    const seconds = Number(p.seconds);
    return { seconds: seconds >= MIN_SECONDS && seconds <= MAX_SECONDS ? seconds : DEFAULT_SECONDS };
  } catch { return { seconds: DEFAULT_SECONDS }; }
};
const savePrefs = (p) => { try { localStorage.setItem(PREFS_KEY, JSON.stringify(p)); } catch { /* ignore */ } };

const STOP = new Set(['the', 'and', 'for', 'with', 'that', 'this', 'are', 'was', 'has', 'have',
  'its', 'can', 'not', 'you', 'your', 'when', 'how', 'what', 'why', 'their', 'them', 'then',
  'than', 'into', 'over', 'from', 'each', 'per', 'via', 'use', 'used', 'using', 'one']);
const tokens = (s) => String(s || '').toLowerCase().replace(/[^a-z0-9 ]/g, ' ')
  .split(/\s+/).filter((w) => w.length > 2 && !STOP.has(w));

// Offline fallback judge (keyword overlap) — used only when the grader is
// unreachable, and to write memory when the learner leaves before grading.
const judge = (q, answer) => {
  const a = new Set(tokens(answer));
  if (!a.size) return false;
  if (q.kind === 'behavioral') return String(answer).trim().length >= 20;
  const ref = tokens(q.reference_answer);
  if (!ref.length) return true;
  const hit = ref.filter((w) => a.has(w)).length;
  return hit / ref.length >= 0.35;
};

const shuffle = (list) => {
  const out = list.slice();
  for (let k = out.length - 1; k > 0; k -= 1) {
    const j = Math.floor(Math.random() * (k + 1));
    [out[k], out[j]] = [out[j], out[k]];
  }
  return out;
};

const SpeechRecognition = typeof window !== 'undefined'
  ? (window.SpeechRecognition || window.webkitSpeechRecognition || null) : null;

const isTypingTarget = (el) => !!el && (el.tagName === 'INPUT' || el.tagName === 'TEXTAREA' || el.tagName === 'BUTTON' || el.isContentEditable);

const BlitzRound = ({ config, onConfigure, onExit, registerLeaveGuard }) => {
  const cids = useMemo(() => scopeIds(config), [config]);
  const { choose } = useDialog();
  const [prefs, setPrefs] = useState(loadPrefs);
  const [qs, setQs] = useState(null);          // the scope's question pool: null = loading, [] = none
  const [fromCache, setFromCache] = useState(false);
  const [err, setErr] = useState('');
  const [phase, setPhase] = useState('setup'); // 'setup' | 'play' | 'grading' | 'done'
  const [list, setList] = useState([]);        // this round's play order (skips move items to the end)
  const [i, setI] = useState(0);
  const [input, setInput] = useState('');
  const [answers, setAnswers] = useState([]);  // {q, status:'answered'|'timeout'|'blank', answer}
  const [graded, setGraded] = useState(null);  // answered entries + {correct, score, feedback}
  const [remaining, setRemaining] = useState([]); // questions never reached when the round ended early
  const [gradeNote, setGradeNote] = useState('');
  const [timeLeft, setTimeLeft] = useState(DEFAULT_SECONDS);
  const [listening, setListening] = useState(false);
  const [speechNote, setSpeechNote] = useState('');
  const inputRef = useRef(null);
  const answersRef = useRef([]);
  const listRef = useRef([]);
  const pausedRef = useRef(false);
  const recRef = useRef(null);
  const mediaRef = useRef(null);
  const dictBaseRef = useRef('');

  /* ── questions: cached per topic scope ────────────────────────────────── */
  const loadQuestions = useCallback((fresh = false) => {
    setErr('');
    if (!fresh) {
      const cached = sessionCache.peekAll(cids, 'blitz');
      if (cached.length) { setQs(cached); setFromCache(true); return; }
    } else {
      sessionCache.takeAll(cids, 'blitz');
    }
    setQs(null); setFromCache(false);
    APIService.engineRapidfire({ conceptIds: cids, count: ROUND_SIZE })
      .then((res) => {
        const got = res.questions || [];
        if (got.length) sessionCache.seed(cids, { blitz: got });
        setQs(got);
      })
      .catch((e) => { setErr(e.message || 'Could not build the round.'); setQs([]); });
  }, [cids]);
  useEffect(() => { if (cids.length) loadQuestions(false); else setQs([]); }, [cids, loadQuestions]);

  const q = phase === 'play' ? list[i] : null;
  const limit = q && q.kind === 'behavioral' ? Math.round(prefs.seconds * STAR_FACTOR) : prefs.seconds;

  /* ── dictation (streaming speech-to-text) ─────────────────────────────── */
  const stopListening = useCallback(() => {
    if (recRef.current) { try { recRef.current.stop(); } catch { /* ignore */ } recRef.current = null; }
    if (mediaRef.current) { try { mediaRef.current.stop(); } catch { /* ignore */ } }
    setListening(false);
  }, []);

  const startListening = useCallback(async () => {
    if (listening) return;
    setSpeechNote('');
    dictBaseRef.current = (inputRef.current?.value || '').trim();
    if (SpeechRecognition) {
      // Browser-native streaming recognition: interim results land in the box
      // as you speak; Enter sends whatever is there.
      const rec = new SpeechRecognition();
      rec.continuous = true; rec.interimResults = true; rec.lang = navigator.language || 'en-US';
      rec.onresult = (e) => {
        const text = Array.from(e.results).map((r) => (r[0] && r[0].transcript ? r[0].transcript.trim() : '')).filter(Boolean).join(' ');
        setInput((dictBaseRef.current ? `${dictBaseRef.current} ` : '') + text);
      };
      rec.onerror = (e) => {
        if (e.error === 'not-allowed' || e.error === 'service-not-allowed') setSpeechNote('Microphone blocked — allow it in the browser to dictate.');
        else if (e.error !== 'aborted' && e.error !== 'no-speech') setSpeechNote('Dictation stopped — try again.');
        recRef.current = null; setListening(false);
      };
      rec.onend = () => { recRef.current = null; setListening(false); };
      try { rec.start(); recRef.current = rec; setListening(true); } catch { setSpeechNote('Dictation could not start.'); }
      return;
    }
    // Fallback (no Web Speech API): record, then transcribe on the server when you stop.
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mr = new MediaRecorder(stream);
      const chunks = [];
      mr.ondataavailable = (ev) => { if (ev.data && ev.data.size) chunks.push(ev.data); };
      mr.onstop = () => {
        stream.getTracks().forEach((t) => t.stop());
        mediaRef.current = null;
        const blob = new Blob(chunks, { type: mr.mimeType || 'audio/webm' });
        const reader = new FileReader();
        reader.onloadend = async () => {
          try {
            const res = await APIService.transcribeAudio(String(reader.result).split(',')[1]);
            const text = (res && (res.transcription || res.text)) || '';
            if (text) setInput((dictBaseRef.current ? `${dictBaseRef.current} ` : '') + text.trim());
          } catch { setSpeechNote('Transcription failed — type your answer.'); }
        };
        reader.readAsDataURL(blob);
      };
      mr.start(); mediaRef.current = mr; setListening(true);
      setSpeechNote('Recording — press Space or the mic again to transcribe.');
    } catch { setSpeechNote('Microphone unavailable — type your answer.'); }
  }, [listening]);

  useEffect(() => () => stopListening(), [stopListening]);   // unmount: release the mic

  /* ── round flow ───────────────────────────────────────────────────────── */
  const start = useCallback(() => {
    if (!qs || !qs.length) return;
    stopListening();
    const order = shuffle(qs);
    listRef.current = order;
    setList(order); setI(0); setInput(''); setGraded(null); setGradeNote(''); setRemaining([]);
    answersRef.current = []; setAnswers([]);
    setPhase('play');
  }, [qs, stopListening]);

  const finish = useCallback(async (entries) => {
    stopListening();
    // Every asked question has exactly one entry, so whatever follows in the
    // play order was never reached — shown, unassessed, with its answer.
    setRemaining(listRef.current.slice(entries.length));
    const answered = entries.filter((a) => a.status === 'answered' && a.answer);
    setPhase('grading');
    let results;
    let note = '';
    if (answered.length) {
      try {
        const res = await APIService.engineBlitzGrade({
          items: answered.map((a) => ({
            question: a.q.question, reference_answer: a.q.reference_answer || '', answer: a.answer,
            kind: a.q.kind || 'technical', concept_ids: a.q.concept_ids || [],
          })),
        });
        const rs = res.results || [];
        results = answered.map((a, k) => {
          const r = rs[k] || {};
          const correct = typeof r.correct === 'boolean' ? r.correct : judge(a.q, a.answer);
          return { ...a, correct, score: typeof r.score === 'number' ? r.score : (correct ? 100 : 0), feedback: r.feedback || '' };
        });
      } catch {
        results = answered.map((a) => { const c = judge(a.q, a.answer); return { ...a, correct: c, score: c ? 100 : 0, feedback: '' }; });
        note = 'Graded by keyword match — the AI grader was unreachable.';
      }
      results.forEach((r) => logRapidfireQuestion(r.q.concept_ids || [], r.correct ? 'correct' : 'wrong'));
      const correct = results.filter((r) => r.correct).length;
      logRapidfireRound({
        rapidfire: true, blitz: true, total: results.length, correct,
        score: Math.round((correct / results.length) * 100),
        question_evaluations: results.map((r) => ({
          question: r.q.question, status: r.correct ? 'correct' : 'wrong',
          score: r.score, concept_ids: r.q.concept_ids || [],
        })),
      }, cids);
    } else {
      results = [];
    }
    setGraded(results); setGradeNote(note);
    setPhase('done');
  }, [cids, stopListening]);

  // Record the current question's outcome and move on — no verdict mid-round.
  const commit = useCallback((status, answer) => {
    const text = String(answer || '').trim();
    const entry = { q: list[i], status: text ? 'answered' : status, answer: text };
    const next = [...answersRef.current, entry];
    answersRef.current = next; setAnswers(next);
    stopListening(); setInput('');
    if (i + 1 >= list.length) finish(next); else setI(i + 1);
  }, [list, i, finish, stopListening]);

  const submit = useCallback(() => {
    if (phase !== 'play' || !q || !input.trim()) return;
    commit('answered', input);
  }, [phase, q, input, commit]);

  const skip = useCallback(() => {
    if (phase !== 'play' || i + 1 >= list.length) return;   // last one: nowhere to bank it
    setList((cur) => { const next = cur.slice(); const [banked] = next.splice(i, 1); next.push(banked); listRef.current = next; return next; });
    stopListening(); setInput('');
  }, [phase, list.length, i, stopListening]);

  const endRound = useCallback(() => {
    if (answersRef.current.some((a) => a.status === 'answered' && a.answer)) finish(answersRef.current);
    else { stopListening(); setPhase('setup'); }
  }, [finish, stopListening]);

  /* ── the clock: tick-based (never wall clock), paused while a dialog is up ─ */
  const leftRef = useRef(DEFAULT_SECONDS);
  useEffect(() => {
    if (phase !== 'play' || !q) return undefined;
    leftRef.current = limit;
    setTimeLeft(limit);
    let iv = null;
    let fired = false;
    const pre = setTimeout(() => {
      iv = setInterval(() => {
        if (pausedRef.current) return;
        leftRef.current -= 0.1;
        if (leftRef.current <= 0) {
          if (fired) return;
          fired = true;
          clearInterval(iv); iv = null;
          commit('timeout', inputRef.current?.value || '');
        } else {
          setTimeLeft(leftRef.current);
        }
      }, 100);
    }, READ_MS);
    return () => { clearTimeout(pre); if (iv) clearInterval(iv); };
    // `commit` changes with `i`, which is exactly when the clock must restart.
  }, [phase, i, q, limit, commit]);

  useEffect(() => { if (phase === 'play' && inputRef.current) inputRef.current.focus(); }, [phase, i]);

  // Space toggles dictation when focus is not in a text field (e.g. after a click).
  useEffect(() => {
    if (phase !== 'play') return undefined;
    const onKey = (e) => {
      if (e.code !== 'Space' || e.repeat || isTypingTarget(document.activeElement)) return;
      e.preventDefault();
      if (listening) stopListening(); else startListening();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [phase, listening, startListening, stopListening]);

  /* ── leaving mid-round ────────────────────────────────────────────────── */
  useEffect(() => {
    if (!registerLeaveGuard) return undefined;
    if (phase !== 'play') { registerLeaveGuard(null); return undefined; }
    registerLeaveGuard(async () => {
      const answered = answersRef.current.filter((a) => a.status === 'answered' && a.answer).length;
      pausedRef.current = true;
      const pick = await choose({
        title: 'Leave the blitz round?',
        message: answered
          ? `You've answered ${answered} question${answered === 1 ? '' : 's'}. See how you did, or leave without an assessment.`
          : 'Nothing answered yet — leaving discards this round.',
        options: answered
          ? [
            { value: 'assess', label: 'See assessment', primary: true },
            { value: 'leave', label: 'Leave (skip assessment)', danger: true },
            { value: 'cancel', label: 'Cancel', ghost: true },
          ]
          : [
            { value: 'leave', label: 'Leave', danger: true },
            { value: 'cancel', label: 'Cancel', ghost: true },
          ],
      });
      pausedRef.current = false;
      if (pick === 'assess') { finish(answersRef.current); return false; }
      if (pick === 'leave') {
        // Skip the assessment, but what was answered still teaches the memory.
        answersRef.current.filter((a) => a.status === 'answered' && a.answer)
          .forEach((a) => logRapidfireQuestion(a.q.concept_ids || [], judge(a.q, a.answer) ? 'correct' : 'wrong'));
        stopListening();
        return true;
      }
      inputRef.current?.focus();
      return false;
    });
    return () => registerLeaveGuard(null);
  }, [phase, registerLeaveGuard, choose, finish, stopListening]);

  useEffect(() => {
    if (phase !== 'play') return undefined;
    const warn = (e) => { e.preventDefault(); e.returnValue = ''; };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [phase]);

  /* ── render ───────────────────────────────────────────────────────────── */
  if (!cids.length) {
    return <ModeEmpty title="No topics configured"
      body="Choose topics in Session Settings and a blitz round will be built from your Knowledge Hub."
      scene="rapidfire" eyebrow="Timed recall"
      onConfigure={onConfigure} />;
  }
  if (qs && !qs.length && phase === 'setup') {
    return <ModeEmpty title="No blitz questions yet"
      body={err || 'No Knowledge Hub content found for the selected topics.'}
      scene="rapidfire" eyebrow="Timed recall"
      onConfigure={onConfigure} />;
  }

  if (phase === 'setup') {
    const starSeconds = Math.round(prefs.seconds * STAR_FACTOR);
    return (
      <div className="bz-wrap">
        <div className="bz-setup">
          <div className="bz-top">
            <div>
              <div className="bz-title">Blitz Round</div>
              <div className="bz-sub">
                {qs === null ? 'Building your round from your topics…'
                  : `${qs.length} one-liners from your topics${fromCache ? ' · ready' : ''}`}
              </div>
            </div>
          </div>
          <div className="bz-field">
            <div className="bz-field-head">
              <label htmlFor="bz-seconds">Seconds per question</label>
              <span className="bz-field-val">{prefs.seconds}s <small>· STAR items {starSeconds}s</small></span>
            </div>
            <input id="bz-seconds" className="bz-slider" type="range" min={MIN_SECONDS} max={MAX_SECONDS} step={5}
              value={prefs.seconds}
              onChange={(e) => { const p = { seconds: Number(e.target.value) }; setPrefs(p); savePrefs(p); }} />
            <div className="bz-slider-scale"><span>{MIN_SECONDS}s</span><span>{MAX_SECONDS}s</span></div>
          </div>
          <ul className="bz-hints">
            <li><b>Enter</b> sends your answer.</li>
            <li><b>Space</b> starts dictation when the box is empty — words stream in as you speak; Space again stops.</li>
            <li><b>Skip</b> banks a question for the end of the round.</li>
            <li>Answers are graded together at the end — no interruptions mid-round.</li>
          </ul>
          <div className="bz-actions">
            <button className="bz-btn primary" type="button" onClick={start} disabled={!qs || !qs.length}>
              {qs === null ? 'Preparing…' : 'Start round'}
            </button>
            <button className="bz-btn" type="button" onClick={() => loadQuestions(true)} disabled={qs === null}>New questions</button>
            <button className="bz-btn" type="button" onClick={onConfigure}>Change topics</button>
          </div>
          {err && <div className="bz-note">{err}</div>}
        </div>
      </div>
    );
  }

  if (phase === 'grading') {
    return (
      <div className="bz-wrap">
        <InterviewPrelude phase="preparing" variant="blitz" begin={false}
          captions={['Grading your answers…', 'Checking the essentials…']}
          sub="Only what you answered is assessed — skipped and unanswered questions don't count against you." />
      </div>
    );
  }

  if (phase === 'done') {
    const results = graded || [];
    const correct = results.filter((r) => r.correct).length;
    const unanswered = answers.filter((a) => !(a.status === 'answered' && a.answer))
      .concat(remaining.map((rq) => ({ q: rq, status: 'unreached', answer: '' })));
    const timeouts = unanswered.filter((a) => a.status === 'timeout').length;
    return (
      <div className="bz-wrap">
        <div className="bz-summary">
          <div className="bz-sum-head">
            <div className="bz-sum-score" role="img" aria-label={`${correct} of ${results.length} correct`}>{correct}<span>/{results.length}</span></div>
            <div>
              <div className="bz-title">{results.length ? 'Blitz assessment' : 'Round ended'}</div>
              <div className="bz-sub">
                {results.length
                  ? `${results.length} answered · ${Math.round((correct / results.length) * 100)}% · every answer is in your memory log`
                  : 'Nothing was answered, so there is nothing to assess.'}
                {unanswered.length > 0 && ` · ${unanswered.length} not answered${timeouts ? ` (${timeouts} timed out)` : ''}`}
              </div>
              {gradeNote && <div className="bz-note">{gradeNote}</div>}
            </div>
          </div>
          <div className="bz-sum-list">
            {results.map((r, k) => (
              <div key={k} className={`bz-sum-row ${r.correct ? 'correct' : 'wrong'}`}>
                <span className="bz-sum-mark">{r.correct ? '✓' : '✗'}</span>
                <div className="bz-sum-q">
                  <div>{r.q.question}</div>
                  <div className="bz-sum-ans"><b>You:</b> {r.answer}</div>
                  {r.feedback && <div className="bz-sum-fb">{r.feedback}</div>}
                  <div className="bz-sum-a">{r.q.reference_answer}</div>
                </div>
              </div>
            ))}
            {unanswered.length > 0 && (
              <details className="bz-unanswered">
                <summary>Not answered ({unanswered.length}) — not assessed</summary>
                {unanswered.map((a, k) => (
                  <div key={k} className="bz-sum-row muted">
                    <span className="bz-sum-mark">{a.status === 'timeout' ? '⏱' : '–'}</span>
                    <div className="bz-sum-q">
                      <div>{a.q.question}</div>
                      <div className="bz-sum-a">{a.q.reference_answer}</div>
                    </div>
                  </div>
                ))}
              </details>
            )}
          </div>
          <div className="bz-actions">
            <button className="bz-btn primary" onClick={start} type="button">Go again</button>
            <button className="bz-btn" onClick={() => { loadQuestions(true); setPhase('setup'); }} type="button">New questions</button>
            <button className="bz-btn" onClick={onExit} type="button">Done</button>
          </div>
        </div>
      </div>
    );
  }

  // phase === 'play'
  const pct = Math.max(0, timeLeft / limit) * 100;
  const answeredCount = answers.filter((a) => a.status === 'answered' && a.answer).length;
  const canDictate = !!SpeechRecognition || !!(navigator.mediaDevices && window.MediaRecorder);
  return (
    <div className="bz-wrap">
      <div className="bz-top">
        <div>
          <div className="bz-title">Blitz Round</div>
          <div className="bz-sub">Question {i + 1} of {list.length} · {answeredCount} answered · graded at the end</div>
        </div>
        <div className="bz-top-actions">
          <button className="bz-btn tiny" onClick={start} type="button" title="Start this round over">Restart</button>
          <button className="bz-btn tiny" onClick={endRound} type="button" title="Stop here and see the assessment">End round</button>
        </div>
      </div>

      <div className={`bz-timer${timeLeft <= 6 ? ' low' : ''}`}>
        <div className="bz-timer-fill" style={{ width: `${pct}%` }} />
      </div>

      <div className={`bz-card${listening ? ' listening' : ''}`}>
        <div className="bz-chips">
          <span className={`bz-chip kind ${q.kind}`}>{q.kind === 'behavioral' ? 'STAR' : 'Tech'}</span>
          {q.tag && <span className="bz-chip pick">◈ {q.tag}</span>}
          {q.topic && <span className="bz-chip">{q.topic}</span>}
          <span className={`bz-chip clock${timeLeft <= 6 ? ' low' : ''}`}>{Math.ceil(timeLeft)}s</span>
        </div>
        <div className="bz-q">{q.question}</div>
        {q.why && <div className="bz-why">{q.why}</div>}

        <div className="bz-input-row">
          <input
            ref={inputRef}
            className="bz-input"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') { e.preventDefault(); submit(); return; }
              if (e.key === ' ' && (!input.trim() || listening) && !e.repeat) {
                e.preventDefault();
                if (listening) stopListening(); else startListening();
              }
            }}
            placeholder={listening ? 'Listening… speak your answer' : (q.kind === 'behavioral' ? 'Situation — action — result, one breath…' : 'One line — go! (Space to dictate)')}
          />
          {canDictate && (
            <button className={`bz-btn mic${listening ? ' on' : ''}`} type="button"
              aria-pressed={listening} aria-label={listening ? 'Stop dictation' : 'Dictate your answer'}
              title={listening ? 'Stop dictation (Space)' : 'Dictate (Space when the box is empty)'}
              onClick={() => (listening ? stopListening() : startListening())}>
              {listening ? <IconStop size={16} /> : <IconMic size={16} />}
            </button>
          )}
          <button className="bz-btn primary" onClick={submit} disabled={!input.trim()} type="button">Send</button>
          {i + 1 < list.length && <button className="bz-btn" onClick={skip} type="button">Skip</button>}
        </div>
        {speechNote && <div className="bz-note">{speechNote}</div>}
      </div>

      <div className="bz-hint">Enter sends · Space dictates · Skip banks it for the end · answers are graded when the round ends</div>
    </div>
  );
};

export default BlitzRound;
